import Auth, { isLoginIdentityUser, isInvitedUser, isGroupReservationUser, isExpertUser, } from '@streem/auth';
import { AppId, GroupReservationId, InvitationId, SessionId, UserId, } from '@streem/domain-id';
import { createLogger } from '@streem/logger';
import { StreemAPI as API, StreemApiAnalyticEventRegion as EventRegion } from '@streem/api';
import { invariant } from '@streem/toolbox';
const SESSION_STORAGE_KEY = 'session-sid';
const SESSION_TIMEOUT_STORAGE_KEY = 'session-timeout';
const UPLOAD_INTERVAL = 30 * 1000; // 30 sec in milliseconds
const SESSION_TIMEOUT_LENGTH = 30 * 60 * 1000; // 30 min in milliseconds
const REGION_MAP = {
    us: EventRegion.US,
    ca: EventRegion.CA,
    global: EventRegion.GLOBAL,
};
class Analytics {
    get sessionSid() {
        let sessionSid = localStorage.getItem(SESSION_STORAGE_KEY);
        const sessionTimeout = localStorage.getItem(SESSION_TIMEOUT_STORAGE_KEY) || '';
        if (!sessionSid || sessionTimeout < new Date().toISOString()) {
            sessionSid = new SessionId().toString();
            localStorage.setItem(SESSION_STORAGE_KEY, sessionSid);
            this.updateSessionTimeout();
        }
        return sessionSid;
    }
    get configured() {
        return this._configured;
    }
    constructor() {
        this.queuedEvents = [];
        this.logger = createLogger('analytics');
        this._configured = false;
        this.flushQueuedEventsAndEndTimer = async () => {
            await this.postQueuedEvents();
            if (this.queueTimerId) {
                this.logger.info('clearing timer set for postQueuedEvents');
                window.clearTimeout(this.queueTimerId);
            }
        };
        this.recordEvent = (event) => {
            invariant(this.configured, 'Analytics.configure must be called first');
            invariant(Object.keys(event).length === 1, 'Events must have one event attached to them');
            try {
                const fullEvent = {
                    sentAt: new Date(),
                    sessionSid: this.sessionSid,
                    appSid: this.appId?.toString(),
                    userSid: this.userId?.toString(),
                    deviceSid: this.deviceId?.toString(),
                    region: this.region,
                    ...event,
                };
                this.queuedEvents.push(fullEvent);
            }
            catch (e) {
                this.logger.error(e);
            }
            finally {
                this.updateSessionTimeout();
            }
        };
        this.updateSessionTimeout = () => {
            const now = new Date().getTime();
            const timeout = new Date(now + SESSION_TIMEOUT_LENGTH);
            localStorage.setItem(SESSION_TIMEOUT_STORAGE_KEY, timeout.toISOString());
        };
        this.postQueuedEvents = async () => {
            const events = this.queuedEvents.splice(0, this.queuedEvents.length);
            try {
                if (events.length > 0) {
                    const { failures } = await API.analytics.recordEvents({ events });
                    if (failures?.length) {
                        // Failures is incorrectly typed
                        const failedEvents = failures.map(failure => ({
                            event: failure.index !== undefined ? events[failure.index] : {},
                            error: failure.error?.message,
                        }));
                        this.logger.warn(`Attempted to upload ${events.length} events, but ${failures.length} failed.`, failedEvents);
                    }
                }
            }
            catch (e) {
                this.logger.error(`Attempted to upload ${events.length} events, but an unexpected error occurred`, e);
            }
            finally {
                if (this.queueTimerId) {
                    window.clearTimeout(this.queueTimerId);
                }
                this.queueTimerId = window.setTimeout(this.postQueuedEvents, UPLOAD_INTERVAL);
            }
        };
        window.setTimeout(this.postQueuedEvents, UPLOAD_INTERVAL);
        // Try to post events when we might lose context
        document.addEventListener('visibilitychange', this.postQueuedEvents);
    }
    configure(config) {
        this.appId = new AppId(config.appId);
        this.region = REGION_MAP[config.regionCode];
        Auth.onAuthStateChanged(user => {
            if (isLoginIdentityUser(user)) {
                // We don't track identified (AKA login identity) users (lid_*), only company/invited users (usr_*)
                return;
            }
            this.userId = isExpertUser(user) ? new UserId(user.id) : undefined;
            this._inviteId = isInvitedUser(user)
                ? new InvitationId(user.invitation.invitationSid)
                : undefined;
            this._groupReservationId = isGroupReservationUser(user)
                ? new GroupReservationId(user.groupReservation.reservationSid)
                : undefined;
        });
        this._configured = true;
    }
    setDeviceId(deviceId) {
        this.deviceId = deviceId;
    }
}
export default new Analytics();
//# sourceMappingURL=analytics.js.map